import { useEffect, useMemo, useRef, useState } from "react";
import {
  Copy,
  GripVertical,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWorkspaceStore } from "@/store/workspace-store";
import {
  analysisTemplatesApi,
  type AnalysisTemplate,
  type BuiltinAnalysisTemplate,
} from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";
import {
  ANALYSIS_ICON_NAMES,
  analysisIcon,
} from "@/components/deal-detail/analysis/analysis-icons";
import { DraftAnalysisDialog } from "./draft-analysis-dialog";
import {
  BLANK_NAME,
  BLANK_SECTION_TITLE,
  isUntouchedBlank,
  type Draft,
  type DraftSection,
} from "./analysis-drafts";
import { cn } from "@/lib/utils";

/**
 * Settings → Analysis.
 *
 * An analysis is a name, an icon and an ordered set of sections, each a title
 * and a prompt. Generating one on a deal runs the sections in order and produces
 * an editable document.
 *
 * Built-in starters are read-only and shipped in code. They exist because the
 * hardest part of this feature is writing the first prompt — someone opening an
 * empty tab has nothing to react to.
 */

// Shared with the deal's Analysis tab and with what the AI drafter may choose,
// so an analysis looks the same wherever it appears.
const iconFor = analysisIcon;

let keySeed = 0;
const nextKey = () => `draft-${keySeed++}`;

const toDraft = (template: AnalysisTemplate): Draft => ({
  key: nextKey(),
  _id: template._id,
  name: template.name,
  icon: template.icon ?? "FileText",
  copied_from: template.copied_from,
  sections: [...template.sections]
    .sort((a, b) => a.order - b.order)
    .map((section) => ({
      key: nextKey(),
      _id: section._id,
      title: section.title,
      prompt: section.prompt,
    })),
});

const toTemplate = (draft: Draft, index: number): AnalysisTemplate => ({
  ...(draft._id ? { _id: draft._id } : {}),
  name: draft.name,
  icon: draft.icon,
  order: index,
  ...(draft.copied_from ? { copied_from: draft.copied_from } : {}),
  sections: draft.sections.map((section, sectionIndex) => ({
    ...(section._id ? { _id: section._id } : {}),
    title: section.title,
    prompt: section.prompt,
    order: sectionIndex,
  })),
});

export default function AnalysisSettings() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [builtins, setBuiltins] = useState<BuiltinAnalysisTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);

  /*
   * A new analysis is appended to the end of a list that is usually longer than
   * the viewport, so on screen nothing happened — and the natural response to
   * nothing happening is to click again. Every click added another row named
   * "New analysis", indistinguishable from the last.
   *
   * `reveal` names the row to bring to the reader once React has rendered it:
   * scrolled to and expanded. `focusName` only for a blank, where renaming it
   * is the next thing to do — a drafted or duplicated analysis arrives already
   * named, and its prompts are what wants reading, so taking the cursor to the
   * name field would point at the wrong thing.
   */
  const [reveal, setReveal] = useState<{
    key: string;
    focusName: boolean;
  } | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!reveal) return;
    rowRefs.current.get(reveal.key)?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
    if (reveal.focusName) {
      // `preventScroll` so focusing doesn't jump instantly to the row and undo
      // the smooth scroll that is the whole point of the gesture.
      nameInputRef.current?.focus({ preventScroll: true });
      nameInputRef.current?.select();
    }
    setReveal(null);
  }, [reveal]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        const { templates, builtins: starters } =
          await analysisTemplatesApi.list(activeWorkspaceId);
        if (cancelled) return;
        setDrafts(
          [...templates].sort((a, b) => a.order - b.order).map(toDraft)
        );
        setBuiltins(starters);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Couldn't load your agents"));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId]);

  /**
   * Save, and hand back the re-keyed drafts.
   *
   * Returns the saved list rather than a boolean because the server re-keys
   * everything on the way back — a caller that wants to point at the row it
   * just created cannot do so with the key it made locally, which no longer
   * exists. `null` on failure; an empty array is a real, successful result.
   */
  const persist = async (next: Draft[]): Promise<Draft[] | null> => {
    if (!activeWorkspaceId) return null;
    setIsSaving(true);
    try {
      const saved = await analysisTemplatesApi.save(
        activeWorkspaceId,
        next.map(toTemplate)
      );
      // Re-read from the response: the server assigns ids to new templates and
      // sections, and documents will reference them.
      const drafted = [...saved].sort((a, b) => a.order - b.order).map(toDraft);
      setDrafts(drafted);
      return drafted;
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't save"));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const duplicateBuiltin = async (starter: BuiltinAnalysisTemplate) => {
    const draft: Draft = {
      key: nextKey(),
      name: `${starter.name}`,
      icon: starter.icon,
      copied_from: starter.id,
      sections: [...starter.sections]
        .sort((a, b) => a.order - b.order)
        .map((section) => ({
          key: nextKey(),
          title: section.title,
          prompt: section.prompt,
        })),
    };

    const saved = await persist([...drafts, draft]);
    if (!saved) return;

    // The copy was appended, so `toTemplate` gave it the highest order and the
    // sort above puts it last. Its local key died in the round trip; this is
    // the one the server handed back in its place.
    const added = saved[saved.length - 1];
    if (added) {
      setOpenKey(added.key);
      setReveal({ key: added.key, focusName: false });
    }
    toast.success(`"${starter.name}" added — edit the prompts to suit your fund`);
  };

  const addBlank = () => {
    // A second click while an untouched blank is already sitting in the list is
    // someone who did not see the first one, not someone who wants two. Show
    // them what they already have rather than stacking an identical row.
    const existing = drafts.find(isUntouchedBlank);
    if (existing) {
      setOpenKey(existing.key);
      setReveal({ key: existing.key, focusName: true });
      toast.info("You already have a blank agent — here it is");
      return;
    }

    const draft: Draft = {
      key: nextKey(),
      name: BLANK_NAME,
      icon: "FileText",
      sections: [
        { key: nextKey(), title: BLANK_SECTION_TITLE, prompt: "" },
      ],
    };
    // Not persisted yet: a section with an empty prompt is rejected by the
    // server, which is correct — it just means this one is saved on Save.
    setDrafts((prev) => [...prev, draft]);
    setOpenKey(draft.key);
    setReveal({ key: draft.key, focusName: true });
  };

  /**
   * Add an AI-drafted analysis, unsaved.
   *
   * Deliberately not persisted — same as `addBlank`. The fund reads the briefs,
   * changes what it got wrong, and saves. Opening it immediately is the point:
   * a draft that scrolls off the bottom of the list will be saved unread.
   */
  const addDrafted = (template: AnalysisTemplate) => {
    const draft = toDraft(template);
    setDrafts((prev) => [...prev, draft]);
    setOpenKey(draft.key);
    setReveal({ key: draft.key, focusName: false });
    toast.success(
      `Drafted "${template.name}" — check the prompts, then Save`,
      { duration: 6000 }
    );
  };

  const remove = async (draft: Draft) => {
    if (await persist(drafts.filter((d) => d.key !== draft.key))) {
      toast.success(`"${draft.name}" deleted`);
    }
  };

  const update = (key: string, change: Partial<Draft>) =>
    setDrafts((prev) =>
      prev.map((draft) => (draft.key === key ? { ...draft, ...change } : draft))
    );

  const updateSection = (
    templateKey: string,
    sectionKey: string,
    change: Partial<DraftSection>
  ) =>
    setDrafts((prev) =>
      prev.map((draft) =>
        draft.key !== templateKey
          ? draft
          : {
              ...draft,
              sections: draft.sections.map((section) =>
                section.key === sectionKey ? { ...section, ...change } : section
              ),
            }
      )
    );

  const alreadyCopied = useMemo(
    () => new Set(drafts.map((d) => d.copied_from).filter(Boolean)),
    [drafts]
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading agents…
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <div>
        <h2 className="font-serif text-2xl tracking-tight">Agents</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Define the agents that analyse a deal for your fund. Each agent writes
          a report made of sections, and each section is a question you want
          answered from the deal&rsquo;s documents.
        </p>
      </div>

      {/* Yours */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Your agents</h3>
          <div className="flex items-center gap-2">
            {activeWorkspaceId && (
              <DraftAnalysisDialog
                workspaceId={activeWorkspaceId}
                onDrafted={addDrafted}
                disabled={isSaving}
              />
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-xs"
              onClick={addBlank}
              disabled={isSaving}
            >
              <Plus className="h-3.5 w-3.5" />
              New agent
            </Button>
          </div>
        </div>

        {drafts.length === 0 && (
          <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            None yet. Describe what you want with{" "}
            <span className="font-medium text-foreground">Draft with AI</span>,
            duplicate a starter below, or create one from scratch.
          </p>
        )}

        <div className="space-y-2">
          {drafts.map((draft) => {
            const Icon = iconFor(draft.icon);
            const isOpen = openKey === draft.key;

            return (
              <div
                key={draft.key}
                ref={(el) => {
                  if (el) rowRefs.current.set(draft.key, el);
                  else rowRefs.current.delete(draft.key);
                }}
                className="rounded-md border border-border"
              >
                <div className="flex items-center gap-2 p-3">
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setOpenKey(isOpen ? null : draft.key)}
                  >
                    <span className="block truncate text-sm font-medium">
                      {draft.name || "Untitled"}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {draft.sections.length}{" "}
                      {draft.sections.length === 1 ? "section" : "sections"}
                    </span>
                  </button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground"
                    onClick={() => remove(draft)}
                    disabled={isSaving}
                    aria-label={`Delete ${draft.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {isOpen && (
                  <div className="space-y-4 border-t border-border p-3">
                    <div className="flex gap-2">
                      <Input
                        // Only one draft is open at a time, so the open one
                        // owns the ref the reveal effect focuses.
                        ref={isOpen ? nameInputRef : undefined}
                        className="h-8 flex-1 text-sm"
                        value={draft.name}
                        placeholder="Name, e.g. IC Memo"
                        onChange={(e) =>
                          update(draft.key, { name: e.target.value })
                        }
                      />
                      <Select
                        value={draft.icon}
                        onValueChange={(icon) => update(draft.key, { icon })}
                      >
                        <SelectTrigger className="h-8 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ANALYSIS_ICON_NAMES.map((name) => {
                            const Option = analysisIcon(name);
                            return (
                              <SelectItem key={name} value={name}>
                                <span className="flex items-center gap-2">
                                  <Option className="h-3.5 w-3.5" />
                                  {name}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      {draft.sections.map((section, index) => (
                        <div
                          key={section.key}
                          className="rounded-md border border-border/60 p-2.5"
                        >
                          <div className="mb-2 flex items-center gap-1.5">
                            <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
                            <span className="w-5 shrink-0 text-xs text-muted-foreground">
                              {index + 1}
                            </span>
                            <Input
                              className="h-8 flex-1 text-sm"
                              value={section.title}
                              placeholder="Section title"
                              onChange={(e) =>
                                updateSection(draft.key, section.key, {
                                  title: e.target.value,
                                })
                              }
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 shrink-0 p-0 text-muted-foreground"
                              aria-label={`Remove ${section.title || "section"}`}
                              onClick={() =>
                                update(draft.key, {
                                  sections: draft.sections.filter(
                                    (s) => s.key !== section.key
                                  ),
                                })
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <Textarea
                            rows={4}
                            className="text-sm"
                            value={section.prompt}
                            placeholder="What should this section answer? Be specific, and say what to do when the documents don't cover it."
                            onChange={(e) =>
                              updateSection(draft.key, section.key, {
                                prompt: e.target.value,
                              })
                            }
                          />
                        </div>
                      ))}

                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-full justify-start gap-1.5 text-xs"
                        onClick={() =>
                          update(draft.key, {
                            sections: [
                              ...draft.sections,
                              {
                                key: nextKey(),
                                title: `Section ${draft.sections.length + 1}`,
                                prompt: "",
                              },
                            ],
                          })
                        }
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add section
                      </Button>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="h-8 text-xs"
                        disabled={isSaving}
                        onClick={async () => {
                          if (await persist(drafts)) {
                            toast.success("Saved");
                            setOpenKey(null);
                          }
                        }}
                      >
                        {isSaving && (
                          <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Starters */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">Starters</h3>
          <p className="text-xs text-muted-foreground">
            Duplicate one and edit the prompts. These stay as they are.
          </p>
        </div>

        <div className="space-y-2">
          {builtins.map((starter) => {
            const Icon = iconFor(starter.icon);
            return (
              <div
                key={starter.id}
                className={cn(
                  "flex items-start gap-3 rounded-md border border-border p-3",
                  alreadyCopied.has(starter.id) && "opacity-60"
                )}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{starter.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {starter.description}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground/70">
                    {starter.sections.map((s) => s.title).join(" · ")}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0 gap-1.5 text-xs"
                  onClick={() => duplicateBuiltin(starter)}
                  disabled={isSaving}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {alreadyCopied.has(starter.id) ? "Duplicate again" : "Duplicate"}
                </Button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
